import React, { useRef, useState, useCallback } from 'react';
import { Box, Button, Typography, IconButton } from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  AttachFile as AttachFileIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { formatFileSize } from '../../utils/formatters';

/**
 * FileDropZone — the app's single file-picker primitive.
 *
 * Adds native HTML5 drag-and-drop as an ADDITION to click-to-browse: the user can
 * either click to open the OS file explorer or drag a file onto the zone. It only
 * STAGES / hands the chosen File to the caller via onFile — it never uploads. The
 * caller keeps its own validation (accept/size), upload endpoint, and gating; drop
 * mirrors choose exactly (so a surface that auto-uploads on choose passes an onFile
 * that uploads, and a stage-then-Upload surface passes a set-file callback).
 *
 * Props:
 *   file        — the staged File (or null); when set, shows name + size + clear (×)
 *   onFile      — (File|null) => void; called on choose, drop, or clear
 *   accept      — input `accept` string (per surface; filters the file picker)
 *   disabled    — block interaction (e.g. upload in flight, or slot already done)
 *   label       — small caption shown above the zone
 *   helperText  — hint under the prompt (e.g. "PDF, DOC or DOCX. Max 10 MB.")
 *   error       — optional error string (renders red under the zone)
 *   variant     — 'box' (default dashed drop area) | 'button' (compact dashed button)
 *   dense       — tighter padding for inline rows
 *   promptText  — override the idle prompt text
 */
const FileDropZone = ({
  file = null,
  onFile,
  accept,
  disabled = false,
  label,
  helperText,
  error,
  variant = 'box',
  dense = false,
  promptText,
}) => {
  const inputRef = useRef(null);
  // Enter/leave counter so dragging over nested children doesn't flicker the state.
  const dragDepth = useRef(0);
  const [isDragging, setIsDragging] = useState(false);

  const openPicker = useCallback(() => {
    if (!disabled) inputRef.current?.click();
  }, [disabled]);

  const handleInputChange = (e) => {
    const f = e.target.files?.[0] || null;
    // Reset so choosing the same file again re-fires onChange.
    e.target.value = '';
    if (f) onFile?.(f);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    if (inputRef.current) inputRef.current.value = '';
    onFile?.(null);
  };

  const stop = (e) => { e.preventDefault(); e.stopPropagation(); };

  const handleDragEnter = (e) => {
    stop(e);
    if (disabled) return;
    dragDepth.current += 1;
    setIsDragging(true);
  };
  const handleDragOver = (e) => {
    stop(e); // required for onDrop to fire
    if (!disabled && !isDragging) setIsDragging(true);
  };
  const handleDragLeave = (e) => {
    stop(e);
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setIsDragging(false);
  };
  const handleDrop = (e) => {
    stop(e);
    dragDepth.current = 0;
    setIsDragging(false);
    if (disabled) return;
    const f = e.dataTransfer?.files?.[0]; // first file only
    if (f) onFile?.(f);
  };

  const dropHandlers = {
    onDragEnter: handleDragEnter,
    onDragOver: handleDragOver,
    onDragLeave: handleDragLeave,
    onDrop: handleDrop,
  };

  const hiddenInput = (
    <input
      ref={inputRef}
      type="file"
      accept={accept}
      onChange={handleInputChange}
      style={{ display: 'none' }}
    />
  );

  // Border colour: dragging (primary) > staged (success) > idle (divider).
  const borderColor = isDragging ? 'primary.main' : (file ? 'success.main' : 'divider');

  const stagedInline = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, overflow: 'hidden' }}>
      <Box sx={{ flex: 1, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {file?.name}{' '}
        {file?.size != null && (
          <Typography component="span" variant="caption" color="text.secondary">({formatFileSize(file.size)})</Typography>
        )}
      </Box>
      {!disabled && (
        <IconButton size="small" onClick={handleClear} component="span" aria-label="Remove file">
          <CloseIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );

  // ---- 'button' variant: compact outlined dashed button (ProofUploadField look) ----
  if (variant === 'button') {
    return (
      <Box>
        {label && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            {label}
          </Typography>
        )}
        <Box {...dropHandlers}>
          <Button
            variant="outlined"
            fullWidth
            onClick={openPicker}
            disabled={disabled}
            startIcon={file ? <AttachFileIcon /> : <CloudUploadIcon />}
            sx={{
              justifyContent: 'flex-start',
              borderStyle: 'dashed',
              borderColor,
              textTransform: 'none',
              py: dense ? 1 : 1.5,
              bgcolor: isDragging ? 'action.hover' : 'transparent',
            }}
          >
            {file ? stagedInline : (
              <Typography variant="body2" color="text.secondary">
                {promptText || 'Click to attach a document, or drag and drop'}
              </Typography>
            )}
            {hiddenInput}
          </Button>
        </Box>
        {(helperText || error) && (
          <Typography variant="caption" color={error ? 'error' : 'text.secondary'} sx={{ display: 'block', mt: 0.5 }}>
            {error || helperText}
          </Typography>
        )}
      </Box>
    );
  }

  // ---- 'box' variant: dashed drop area (default) ----
  return (
    <Box>
      {label && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          {label}
        </Typography>
      )}
      <Box
        {...dropHandlers}
        onClick={openPicker}
        sx={{
          border: '2px dashed',
          borderColor,
          borderRadius: 1,
          p: dense ? 1.5 : 3,
          textAlign: 'center',
          cursor: disabled ? 'default' : 'pointer',
          bgcolor: isDragging ? 'action.hover' : 'transparent',
          opacity: disabled ? 0.6 : 1,
          transition: 'border-color 120ms, background-color 120ms',
          '&:hover': disabled ? {} : { borderColor: 'primary.main' },
        }}
      >
        {hiddenInput}
        {file ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
            <AttachFileIcon fontSize="small" color="action" />
            <Typography variant="body2" fontWeight="medium" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {file.name}
            </Typography>
            {file.size != null && (
              <Typography variant="caption" color="text.secondary">({formatFileSize(file.size)})</Typography>
            )}
            {!disabled && (
              <IconButton size="small" onClick={handleClear} aria-label="Remove file">
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        ) : (
          <>
            <CloudUploadIcon color="action" sx={{ mb: dense ? 0 : 0.5 }} />
            <Typography variant="body2" color="text.secondary">
              {promptText || 'Click to select a file, or drag and drop'}
            </Typography>
            {helperText && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {helperText}
              </Typography>
            )}
          </>
        )}
      </Box>
      {error && (
        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default FileDropZone;
